import { Colors, Icon, Text, type IconName } from '@amelio/ui-library';

import { recapOutcome } from '../recapOutcome';

import type { MeetingTranscriptLabels } from '../types';
import type { MeetingRecap } from '@/types/review/PostReviewMeeting';

import { getLocalizedText } from '@/utils/localization';

const RecapSectionTitle = ({ title, icon }: { title: string; icon: IconName }) => (
    <div className={'mb-2 flex items-center gap-2'}>
        <Icon
            name={icon}
            size={'sm'}
            color={Colors.BASE_500}
        />
        <Text
            size={'small'}
            weight={'semibold'}
            color={Colors.BASE_500}
        >
            {title}
        </Text>
    </div>
);

const RecapList = ({ title, icon, items }: { title: string; icon: IconName; items: string[] }) => {
    if (items.length === 0) return null;
    return (
        <div>
            <RecapSectionTitle
                title={title}
                icon={icon}
            />
            <ul className={'ml-6 list-disc space-y-1'}>
                {items.map((item, index) => (
                    <li key={index}>
                        <Text
                            size={'small'}
                            color={Colors.BASE_600}
                        >
                            {item}
                        </Text>
                    </li>
                ))}
            </ul>
        </div>
    );
};

interface RecapSectionsProps {
    recap: MeetingRecap;
    labels: MeetingTranscriptLabels;
}

/** What the AI wrote, read in the order the meeting went: the essential, then what was discussed,
 * decided, committed to and left to follow up. When nothing could be written from the file, it says
 * so rather than showing an empty card; when only part of the meeting could be read, it says that. */
export const RecapSections = ({ recap, labels }: RecapSectionsProps) => {
    const outcome = recapOutcome(recap);

    if (outcome === 'unusable') {
        return (
            <div
                className={'space-y-1'}
                data-testid={'meeting-recap-unusable'}
            >
                <Text
                    size={'small'}
                    weight={'semibold'}
                    color={Colors.BASE_600}
                >
                    {labels.unusableTitle}
                </Text>
                <Text
                    size={'small'}
                    color={Colors.BASE_600}
                >
                    {labels.unusableBody}
                </Text>
            </div>
        );
    }

    return (
        <div
            className={'space-y-4'}
            data-testid={'meeting-recap'}
        >
            <Text
                size={'small'}
                color={Colors.BASE_600}
            >
                {getLocalizedText(recap.essential)}
            </Text>

            {outcome === 'incomplete' && (
                <div data-testid={'meeting-recap-disclosure'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_500}
                    >
                        {labels.disclosure}
                    </Text>
                </div>
            )}

            <RecapList
                title={labels.discussedPoints}
                icon={'lucide-clipboard-list'}
                items={recap.discussedPoints.map((p) => getLocalizedText(p))}
            />

            <RecapList
                title={labels.decisions}
                icon={'essential-tick-circle'}
                items={recap.decisions.map((d) => getLocalizedText(d))}
            />

            {recap.commitments.length > 0 && (
                <div>
                    <RecapSectionTitle
                        title={labels.commitments}
                        icon={'essential-flag'}
                    />
                    <ul className={'ml-6 list-disc space-y-1'}>
                        {recap.commitments.map((c, index) => {
                            const due = getLocalizedText(c.dueDate);
                            return (
                                <li key={index}>
                                    <Text
                                        size={'small'}
                                        color={Colors.BASE_600}
                                    >
                                        {getLocalizedText(c.action)}
                                        {' — '}
                                        <span className={'italic'}>
                                            {labels.commitmentOwner(c.owner)}
                                        </span>
                                        {due ? ` (${due})` : ''}
                                    </Text>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <RecapList
                title={labels.followUps}
                icon={'arrow-arrow-right'}
                items={recap.followUps.map((f) => getLocalizedText(f))}
            />
        </div>
    );
};
